import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Manages service-worker registration + Web Push subscription.
 *
 * Returns:
 *   - permission  "default" | "granted" | "denied" | "unsupported"
 *   - enable()    call on a user gesture to request permission + subscribe
 *   - disable()   unsubscribe from push
 */
export function usePushNotifications(api) {
  const [permission, setPermission] = useState("default");
  const registrationRef = useRef(null);
  const vapidKeyRef = useRef(null);
  const subscribedRef = useRef(false);

  // Check current state on mount
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setPermission("unsupported");
      return;
    }
    setPermission(Notification.permission); // "default" | "granted" | "denied"

    // Fetch VAPID key and register SW proactively (no permission needed)
    (async () => {
      try {
        const { data } = await api.get("/push/vapid-key").catch(() => ({ data: null }));
        if (!data?.publicKey) return;
        vapidKeyRef.current = data.publicKey;

        const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
        registrationRef.current = reg;

        // If already granted, re-sync subscription silently
        if (Notification.permission === "granted") {
          await syncSubscription(api, reg, data.publicKey);
          subscribedRef.current = true;
        }
      } catch {}
    })();
  }, [api]);

  const enable = useCallback(async () => {
    if (!("Notification" in window)) return "unsupported";
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") return result;

      // Register SW if not yet done
      let reg = registrationRef.current;
      if (!reg) {
        reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
        registrationRef.current = reg;
      }

      let publicKey = vapidKeyRef.current;
      if (!publicKey) {
        const { data } = await api.get("/push/vapid-key").catch(() => ({ data: null }));
        if (!data?.publicKey) return result;
        publicKey = data.publicKey;
        vapidKeyRef.current = publicKey;
      }

      await syncSubscription(api, reg, publicKey);
      subscribedRef.current = true;

      // Send a test notification so user knows it works
      new Notification("Safe Zap — Notificações ativadas! 🔔", {
        body: "Você receberá alertas de novas mensagens mesmo com a aba fechada.",
        icon: "/icon-192.png",
      });

      return result;
    } catch {
      return "denied";
    }
  }, [api]);

  const disable = useCallback(async () => {
    try {
      const reg = registrationRef.current || await navigator.serviceWorker.getRegistration("/sw.js");
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } }).catch(() => {});
          await sub.unsubscribe();
        }
      }
      subscribedRef.current = false;
    } catch {}
  }, [api]);

  return { permission, enable, disable };
}

async function syncSubscription(api, reg, publicKey) {
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  await api.post("/push/subscribe", {
    endpoint: sub.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(sub.getKey("p256dh")),
      auth: arrayBufferToBase64(sub.getKey("auth")),
    },
  });
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function arrayBufferToBase64(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}
